import type { LayoutActionsContextValue } from "@/contexts/layout/LayoutActionsProvider";
import type {
  ReferenceNavigationApi,
  ReferenceNavigationHandler,
} from "@/contexts/session/ui/use-reference-navigation";
import { LogLevel, type useSeqLog } from "@/hooks/telemetry";
import { CREFERENCE_ENTITY_TYPE } from "./CreferenceEntity";
import type {
  InternalReferenceAction,
  InternalReferenceActionMap,
  ReferenceCollab,
  ReferenceFile,
  ReferenceMember,
  ReferenceProduct,
  ReferenceVersion,
} from "./creferenceInteractionTypes";

type CreferenceSeqLog = ReturnType<typeof useSeqLog>;

interface BuildCreferenceInternalActionsInput {
  collab: ReferenceCollab | null;
  file: ReferenceFile | null;
  isCurrentUserReference: boolean;
  layoutActions: LayoutActionsContextValue | null;
  member: ReferenceMember | null;
  normalizedEntityId: string;
  product: ReferenceProduct | null;
  referenceNavigation: ReferenceNavigationApi;
  seqLog: CreferenceSeqLog;
  showMemberGlimpse: () => void;
  version: ReferenceVersion | null;
}

interface NavigationActionInput {
  navigate: ReferenceNavigationHandler;
  normalizedEntityId: string;
  seqLog: CreferenceSeqLog;
}

interface FileNavigationActionInput {
  navigate: ReferenceNavigationHandler;
  normalizedEntityId: string;
}

interface CollabNavigationActionInput extends NavigationActionInput {
  navigate: ReferenceNavigationHandler;
}

interface MemberActionInput {
  showMemberGlimpse: () => void;
}

interface UserActionInput {
  layoutActions: LayoutActionsContextValue | null;
}

function setInternalReferenceAction(input: {
  action: InternalReferenceAction;
  actions: InternalReferenceActionMap;
  enabled: boolean;
  type: keyof InternalReferenceActionMap;
}): void {
  if (!input.enabled) {
    return;
  }

  input.actions[input.type] = input.action;
}

export function buildCreferenceInternalActions(
  input: BuildCreferenceInternalActionsInput,
): InternalReferenceActionMap {
  const actions: InternalReferenceActionMap = {};

  setInternalReferenceAction({
    action: createProductReferenceAction({
      navigate: input.referenceNavigation.navigateToProductReference,
      normalizedEntityId: input.normalizedEntityId,
      seqLog: input.seqLog,
    }),
    actions,
    enabled: Boolean(input.product),
    type: CREFERENCE_ENTITY_TYPE.PRODUCT,
  });
  setInternalReferenceAction({
    action: createVersionReferenceAction({
      navigate: input.referenceNavigation.navigateToVersionReference,
      normalizedEntityId: input.normalizedEntityId,
      seqLog: input.seqLog,
    }),
    actions,
    enabled: Boolean(input.version),
    type: CREFERENCE_ENTITY_TYPE.VERSION,
  });
  setInternalReferenceAction({
    action: createFileReferenceAction({
      navigate: input.referenceNavigation.navigateToFileReference,
      normalizedEntityId: input.normalizedEntityId,
    }),
    actions,
    enabled: Boolean(input.file),
    type: CREFERENCE_ENTITY_TYPE.FILE,
  });
  setInternalReferenceAction({
    action: createMemberReferenceAction({
      showMemberGlimpse: input.showMemberGlimpse,
    }),
    actions,
    enabled: Boolean(input.member),
    type: CREFERENCE_ENTITY_TYPE.MEMBER,
  });
  setInternalReferenceAction({
    action: createCollabReferenceAction({
      navigate: input.referenceNavigation.navigateToCollabReference,
      normalizedEntityId: input.normalizedEntityId,
      seqLog: input.seqLog,
    }),
    actions,
    enabled: Boolean(input.collab),
    type: CREFERENCE_ENTITY_TYPE.COLLAB,
  });
  setInternalReferenceAction({
    action: createUserReferenceAction({ layoutActions: input.layoutActions }),
    actions,
    enabled: Boolean(
      input.isCurrentUserReference && input.layoutActions?.openUserMenu,
    ),
    type: CREFERENCE_ENTITY_TYPE.USER,
  });

  return actions;
}

function createProductReferenceAction({
  navigate,
  normalizedEntityId,
  seqLog,
}: NavigationActionInput): InternalReferenceAction {
  return () => {
    seqLog("02 Creference.handleClick: dispatching to navigateToProductReference", {
      entityId: normalizedEntityId,
    }, LogLevel.Info);
    return navigate(normalizedEntityId);
  };
}

function createVersionReferenceAction({
  navigate,
  normalizedEntityId,
  seqLog,
}: NavigationActionInput): InternalReferenceAction {
  return () => {
    seqLog("02 Creference.handleClick: dispatching to navigateToVersionReference", {
      entityId: normalizedEntityId,
    }, LogLevel.Info);
    return navigate(normalizedEntityId);
  };
}

function createFileReferenceAction({
  navigate,
  normalizedEntityId,
}: FileNavigationActionInput): InternalReferenceAction {
  return () => {
    return navigate(normalizedEntityId);
  };
}

function createMemberReferenceAction({
  showMemberGlimpse,
}: MemberActionInput): InternalReferenceAction {
  return () => {
    showMemberGlimpse();
  };
}

function createCollabReferenceAction({
  navigate,
  normalizedEntityId,
  seqLog,
}: CollabNavigationActionInput): InternalReferenceAction {
  return () => {
    seqLog("02 Creference.handleClick: dispatching to navigateToCollabReference", {
      entityId: normalizedEntityId,
    }, LogLevel.Info);
    const outcome = navigate(normalizedEntityId);
    seqLog("03 Creference.handleClick: navigateToCollabReference returned", {
      entityId: normalizedEntityId,
      outcome,
    }, LogLevel.Info);
    return outcome;
  };
}

function createUserReferenceAction({
  layoutActions,
}: UserActionInput): InternalReferenceAction {
  return () => {
    layoutActions?.openUserMenu();
  };
}
